import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';

// Dataverse dagrun_pm_boardview schema (verified via EntityDefinitions):
//   dagrun_pm_boardviewid (guid), dagrun_pm_name (string)
//   dagrun_pm_projectid (Lookup to dagrun_pm_project)
//   dagrun_pm_ownerid (string) — AAD OID of the user who owns this view
//   dagrun_pm_groupbyfield (picklist): 0 Status, 1 Assignee, 2 Priority, 3 Label, 4 CustomField
//   dagrun_pm_cardsize (picklist): 0 Compact, 1 Comfortable
//   dagrun_pm_filtersjson (memo) — JSON: { search?, status?, priority?, assignee? }
//   dagrun_pm_isdefaultforuser (boolean)
//   dagrun_pm_defaultexpandnotes / dagrun_pm_defaultexpandchecklists (boolean)
//   dagrun_pm_collectionorderjson / dagrun_pm_customitemidsjson (memo — not used yet)
//   dagrun_pm_hierarchydisplaymode (picklist — not used yet)

export interface BoardView {
  dagrun_pm_boardviewid: string;
  dagrun_pm_name: string;
  _dagrun_pm_projectid_value?: string | null;
  dagrun_pm_ownerid?: string | null;
  dagrun_pm_groupbyfield?: number | null;
  dagrun_pm_cardsize?: number | null;
  dagrun_pm_filtersjson?: string | null;
  dagrun_pm_isdefaultforuser?: boolean | null;
  dagrun_pm_defaultexpandnotes?: boolean | null;
  dagrun_pm_defaultexpandchecklists?: boolean | null;
  dagrun_pm_collectionorderjson?: string | null;
}

// Read-or-create a personal "Implicit" board view that holds per-user
// collection ordering. Used by the board-level collection drag-reorder
// to persist order locally without affecting other users.
export async function getOrCreatePersonalImplicitView(
  pca: IPublicClientApplication,
  projectId: string,
  userId: string,
): Promise<BoardView> {
  const all = await listBoardViews(pca, projectId, userId);
  const existing = all.find(v => v.dagrun_pm_name === '__personal_implicit');
  if (existing) return existing;
  const id = await createBoardView(pca, {
    projectId,
    userId,
    name: '__personal_implicit',
    groupBy: 'status',
    filters: {},
  });
  return { dagrun_pm_boardviewid: id, dagrun_pm_name: '__personal_implicit', dagrun_pm_ownerid: userId };
}

export async function setPersonalCollectionOrder(
  pca: IPublicClientApplication,
  projectId: string,
  userId: string,
  collectionIds: string[],
): Promise<void> {
  const view = await getOrCreatePersonalImplicitView(pca, projectId, userId);
  await updateBoardView(pca, view.dagrun_pm_boardviewid, {
    dagrun_pm_collectionorderjson: JSON.stringify(collectionIds),
  });
}

export async function getPersonalCollectionOrder(
  pca: IPublicClientApplication,
  projectId: string,
  userId: string,
): Promise<string[] | null> {
  try {
    const all = await listBoardViews(pca, projectId, userId);
    const view = all.find(v => v.dagrun_pm_name === '__personal_implicit');
    if (!view?.dagrun_pm_collectionorderjson) return null;
    const arr = JSON.parse(view.dagrun_pm_collectionorderjson);
    return Array.isArray(arr) ? arr.filter(x => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

export const BoardViewGroupBy = {
  Status:      100000000,
  Assignee:    100000001,
  Priority:    100000002,
  Label:       100000003,
  CustomField: 100000004,
} as const;

export const BoardViewCardSize = {
  Compact:     100000000,
  Comfortable: 100000001,
} as const;

// Map between the UI's BoardGroupBy string keys and the Dataverse picklist.
export const groupByToPicklist: Record<string, number> = {
  status:   BoardViewGroupBy.Status,
  assignee: BoardViewGroupBy.Assignee,
  priority: BoardViewGroupBy.Priority,
  phase:    BoardViewGroupBy.Label, // phase → Label bucket on the picklist
};
export const groupByFromPicklist: Record<number, 'status' | 'assignee' | 'priority' | 'phase'> = {
  [BoardViewGroupBy.Status]:      'status',
  [BoardViewGroupBy.Assignee]:    'assignee',
  [BoardViewGroupBy.Priority]:    'priority',
  [BoardViewGroupBy.Label]:       'phase',
  [BoardViewGroupBy.CustomField]: 'phase',
};

const FIELDS = [
  'dagrun_pm_boardviewid', 'dagrun_pm_name', 'dagrun_pm_ownerid',
  'dagrun_pm_groupbyfield', 'dagrun_pm_cardsize', 'dagrun_pm_filtersjson',
  'dagrun_pm_isdefaultforuser',
  'dagrun_pm_defaultexpandnotes', 'dagrun_pm_defaultexpandchecklists',
  'dagrun_pm_collectionorderjson',
  '_dagrun_pm_projectid_value',
];

export async function listBoardViews(pca: IPublicClientApplication, projectId: string, userId: string): Promise<BoardView[]> {
  return dvList<BoardView>(pca, 'dagrun_pm_boardviews', {
    select: FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId} and dagrun_pm_ownerid eq '${userId}'`,
    orderby: 'dagrun_pm_name asc',
    top: 200,
  });
}

export interface CreateBoardViewInput {
  projectId: string;
  userId: string;
  name: string;
  groupBy: 'status' | 'assignee' | 'priority' | 'phase';
  filters: Record<string, unknown>;
  cardSize?: number;
  isDefault?: boolean;
  expandNotes?: boolean;
  expandChecklists?: boolean;
}

export async function createBoardView(pca: IPublicClientApplication, input: CreateBoardViewInput): Promise<string> {
  const body: Record<string, unknown> = {
    dagrun_pm_name: input.name,
    dagrun_pm_ownerid: input.userId,
    dagrun_pm_groupbyfield: groupByToPicklist[input.groupBy] ?? BoardViewGroupBy.Status,
    dagrun_pm_cardsize: input.cardSize ?? BoardViewCardSize.Comfortable,
    dagrun_pm_filtersjson: JSON.stringify(input.filters),
    dagrun_pm_isdefaultforuser: !!input.isDefault,
    dagrun_pm_defaultexpandnotes: !!input.expandNotes,
    dagrun_pm_defaultexpandchecklists: !!input.expandChecklists,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${input.projectId})`,
  };
  return dvCreate(pca, 'dagrun_pm_boardviews', body);
}

export async function updateBoardView(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_boardviews', id, patch);
}

export async function deleteBoardView(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_boardviews', id);
}

// Make `id` the only default for this user+project. Clears the default flag
// on any other view owned by this user on this project, then sets it on the
// chosen view. Safe to call even when the target view is already default.
export async function setDefaultBoardView(pca: IPublicClientApplication, id: string, projectId: string, userId: string): Promise<void> {
  const existing = await listBoardViews(pca, projectId, userId);
  for (const v of existing) {
    if (v.dagrun_pm_boardviewid === id) continue;
    if (v.dagrun_pm_isdefaultforuser) {
      try { await updateBoardView(pca, v.dagrun_pm_boardviewid, { dagrun_pm_isdefaultforuser: false }); }
      catch { /* best effort */ }
    }
  }
  return updateBoardView(pca, id, { dagrun_pm_isdefaultforuser: true });
}

export interface BoardViewSnapshot {
  groupBy: 'status' | 'assignee' | 'priority' | 'phase';
  filters: Record<string, unknown>;
  cardSize?: number;
  expandNotes?: boolean;
  expandChecklists?: boolean;
}

export function deserialize(view: BoardView): BoardViewSnapshot {
  let filters: Record<string, unknown> = {};
  try { filters = view.dagrun_pm_filtersjson ? JSON.parse(view.dagrun_pm_filtersjson) : {}; }
  catch { filters = {}; }
  return {
    groupBy: view.dagrun_pm_groupbyfield != null
      ? (groupByFromPicklist[view.dagrun_pm_groupbyfield] ?? 'status')
      : 'status',
    filters,
    cardSize: view.dagrun_pm_cardsize ?? undefined,
    expandNotes: view.dagrun_pm_defaultexpandnotes ?? undefined,
    expandChecklists: view.dagrun_pm_defaultexpandchecklists ?? undefined,
  };
}
